import React from 'react';
import { Speciality } from '@maiia/model/generated/model/api-patient/api-patient';
import { actionsGeneratorV2, asyncActions } from '@docavenue/core';
import { NextSeo } from 'next-seo';
import { Stack } from '@mui/material';
import InventoryPage from '@/components/templates/InventoryPage/InventoryPage';
import { useTranslation } from '@/src/i18n';
import InventorySection from '@/components/templates/InventoryPage/InventorySection';
import { APP_ROUTES } from '@/src/constants';
import { wrapper } from '@/src/store';

type Props = {
  data: {
    practitioners: GetListApiResponse<Speciality>;
    centers: GetListApiResponse<Speciality>;
  };
};

const LIMIT_PER_PAGE = 90;

const action = actionsGeneratorV2({
  resource: 'specialitiesIndex',
  chunkUrlResource: 'directory/specialities',
  scope: 'pat-public',
});

const noop = () => {};

const SpecialitiesIndex = ({ data }: Props) => {
  const { t } = useTranslation();
  const specialitiesPractitionerItems: Array<{
    name: string;
    url: string;
  }> = data.practitioners.items.map(p => ({
    name: p.name ?? '',
    url: `/${p.shortName}`,
  }));

  const specialitiesCenterItems: Array<{
    name: string;
    url: string;
  }> = data.centers.items.map(p => ({
    name: p.name ?? '',
    url: `/${p.shortName}`,
  }));

  const breadcrumbs = [
    {
      name: 'Maiia',
      url: APP_ROUTES.HOME,
    },
    {
      name: t(`directory_specialities`),
      url: APP_ROUTES.SPECIALITIES.INDEX,
    },
  ];

  return (
    <>
      <NextSeo
        title={t('seo-title_directory_specialities')}
        description={t('seo-description_directory_specialities')}
      />
      <InventoryPage
        breadcrumbs={breadcrumbs}
        showNext={false}
        showPrev={false}
        onNext={noop}
        onPrev={noop}
      >
        <Stack spacing={4} direction="column">
          <InventorySection
            title="directory_specialities"
            items={specialitiesPractitionerItems}
          />
          <InventorySection
            title="health-care-facilities"
            items={specialitiesCenterItems}
          />
        </Stack>
      </InventoryPage>
    </>
  );
};

export const getStaticProps = wrapper.getStaticProps<Props>(
  store => async () => {
    const data = await Promise.all([
      asyncActions(
        store.dispatch,
        action.getList({
          target: 'PRACTITIONER',
          limit: LIMIT_PER_PAGE,
          sort: 'usageRanking',
        }),
      ),
      asyncActions(
        store.dispatch,
        action.getList({
          target: 'CENTER',
          limit: LIMIT_PER_PAGE,
          sort: 'usageRanking',
        }),
      ),
    ]).catch(() => [
      { items: [], total: 0 },
      { items: [], total: 0 },
    ]);

    return {
      props: {
        data: { practitioners: data[0], centers: data[1] },
      },
    };
  },
);

export default SpecialitiesIndex;
